import React from 'react';
import { Row, Col } from 'reactstrap';
import { StatusBadge } from '../../../../../components/Common';
import memberProfileBg from '../../../../../assets/images/member_profile_bg.png';
import ProfileHeaderActions from './ProfileHeaderActions';
import ProfileTabs from '../ProfileTabs';

/**
 * Avatar individual component
 */
const ProfileAvatar = ({ photoUrl, name }) => (
    <div
        style={{
            width: '110px',
            height: '110px',
            borderRadius: '50%',
            border: '4px solid rgba(255,255,255,0.2)',
            backgroundColor: 'rgba(255,255,255,0.1)',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)'
        }}
    >
        {photoUrl ? (
            <img
                src={photoUrl}
                alt={name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
        ) : (
            <i className="mdi mdi-account text-white" style={{ fontSize: '50px', opacity: 0.8 }}></i>
        )}
    </div>
);

/**
 * Basic info/details of the profile
 */
const ProfileBasicInfo = ({ fullName, memberNumber, status }) => (
    <div className="d-flex flex-column h-100 justify-content-center">
        <div className="mb-1">
            <h2 className="fw-bold text-white mb-0 shadow-sm" style={{ letterSpacing: '-0.5px' }}>{fullName}</h2>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
            <span
                className="badge rounded-0 px-3 py-1 font-size-12 fw-bold"
                style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(5px)' }}
            >
                ID: {memberNumber}
            </span>
            <StatusBadge status={status} />
        </div>
    </div>
);

/**
 * Main ProfileHeader component
 * Updated: Increased height to 320px and added translucid tabs inside
 */
const ProfileHeader = ({ member, onEdit, onSave, onCancel, isEditing, onPhotoChange, onNewSale, onPayDebt, totalDebt, activeTab, toggleTab }) => {
    const {
        fullName = "Nome do Cliente",
        memberNumber = "----",
        status = "Ativo",
        photoUrl = null,
        activityNames = [],
        xpTotal = 0
    } = member || {};

    return (
        <div className="profile-header-container mb-4 position-relative rounded-0 overflow-hidden">
            {/* Banner Background */}
            <div
                style={{
                    backgroundImage: `url(${memberProfileBg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    minHeight: '320px', // Increased height to accommodate tabs at bottom
                    width: '100%',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Visual Overlay */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0.7) 100%)',
                    zIndex: 1
                }} />

                {/* Atividades e XP - Canto Superior Direito */}
                <div style={{ position: 'absolute', top: '24px', right: '24px', zIndex: 3 }} className="d-none d-md-flex align-items-center gap-3">
                    {/* XP Indicator */}
                    <div className="d-flex flex-column align-items-end text-end">
                        <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.6)', fontWeight: 'bold' }}>XP</span>
                        <span style={{ fontSize: '22px', fontWeight: '900', color: '#f1b44c', textShadow: '0 2px 4px rgba(0,0,0,0.5)', lineHeight: '1' }}>{xpTotal}</span>
                    </div>

                    <div style={{ height: '35px', width: '1px', backgroundColor: 'rgba(255,255,255,0.2)' }}></div>

                    {/* Activities List */}
                    <div className="d-flex flex-column align-items-end text-end">
                        <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.6)', fontWeight: 'bold' }}>Nível</span>
                        <div className="d-flex flex-wrap gap-1 justify-content-end mt-1">
                            {activityNames && activityNames.length > 0 ? (
                                activityNames.map((name, idx) => (
                                    <span 
                                        key={idx} 
                                        className="badge px-3 py-1"
                                        style={{ 
                                            background: 'linear-gradient(135deg, rgba(85, 110, 230, 0.4), rgba(85, 110, 230, 0.1))',
                                            color: '#fff', 
                                            border: '1px solid rgba(85, 110, 230, 0.5)', 
                                            backdropFilter: 'blur(8px)',
                                            borderRadius: '0px',
                                            fontWeight: '600',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                            fontSize: '11px'
                                        }}
                                    >
                                        {name}
                                    </span>
                                ))
                            ) : (
                                <span className="text-white-50 mt-1" style={{ fontSize: '12px', fontStyle: 'italic' }}>Nenhuma</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Top/Main Content Area */}
                <div className="container-fluid px-4 pt-5 pb-4 position-relative flex-grow-1 d-flex align-items-center" style={{ zIndex: 2 }}>
                    <Row className="align-items-center g-4 w-100">
                            {/* Avatar Area */}
                            <Col xs="auto">
                                <div className="position-relative">
                                    <ProfileAvatar photoUrl={photoUrl} name={fullName} />
                                    {isEditing && (
                                        <div className="position-absolute" style={{ bottom: '-5px', right: '-5px', zIndex: 10 }}>
                                            <label className="btn btn-primary btn-sm rounded-circle d-flex align-items-center justify-content-center p-0 m-0 shadow-lg cursor-pointer" style={{ width: '36px', height: '36px', border: '2px solid white' }}>
                                                <i className="mdi mdi-camera font-size-18"></i>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="d-none"
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            const reader = new FileReader();
                                                            reader.onloadend = () => {
                                                                if (onPhotoChange) onPhotoChange(reader.result);
                                                            };
                                                            reader.readAsDataURL(file);
                                                        }
                                                    }}
                                                />
                                            </label>
                                        </div>
                                    )}
                                </div>
                            </Col>

                        {/* Info Area */}
                        <Col>
                            <ProfileBasicInfo 
                                fullName={fullName} 
                                memberNumber={memberNumber} 
                                status={status} 
                            />
                            {totalDebt > 0 && (
                                <div className="mt-3 d-inline-flex align-items-center bg-warning bg-opacity-10 border border-warning rounded-0 p-2 shadow-none animate__animated animate__pulse">
                                    <div className="me-3">
                                        <span className="d-block text-warning fw-bold font-size-13 mb-0" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}>
                                            <i className="mdi mdi-alert-circle font-size-16 me-1"></i> Cliente com saldo devedor
                                        </span>
                                        <span className="d-block text-warning font-size-15 fw-900 mt-n1">
                                            R$ {totalDebt.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                        </span>
                                    </div>
                                    <button 
                                        className="btn btn-sm btn-soft-warning rounded-0 flex-shrink-0 fw-bold px-3 py-2 text-dark border-0"
                                        onClick={onPayDebt}
                                        title="Quitar Saldo Devedor"
                                    >
                                        <i className="mdi mdi-currency-usd font-size-16 me-1"></i> Quitar
                                    </button>
                                </div>
                            )}
                        </Col>

                        {/* Action Area */}
                        <Col xs="12" md="auto" className="ms-auto align-self-center">
                            <ProfileHeaderActions 
                                onNewSale={onNewSale} 
                                onEdit={onEdit} 
                                onSave={onSave}
                                onCancel={onCancel}
                                isEditing={isEditing}
                            />
                        </Col>
                    </Row>
                </div>

                {/* Tabs Area (At the bottom of the image) */}
                <div className="container-fluid px-4 mt-auto position-relative d-flex align-items-end justify-content-between" style={{ zIndex: 3 }}>
                    <ProfileTabs activeTab={activeTab} toggleTab={toggleTab} />

                    {/* Membership Info (Bottom Right) */}
                    <div className="pb-3 text-end d-none d-md-block">
                        <span
                            className="px-3 py-2 rounded-0"
                            style={{
                                backgroundColor: 'rgba(0,0,0,0.3)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                color: 'rgba(255,255,255,0.8)',
                                fontSize: '12px',
                                fontWeight: '500'
                            }}
                        >
                            <i className="mdi mdi-calendar-check-outline me-2 text-info"></i>
                            Membro desde Jan 2024
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileHeader;
